import { type FC, useEffect, useState } from "react"

import { replaceArrayValue } from "@forento/core/utilities/array"
import { useFile } from "@forento/ui/hooks/useFile"

import Button from "~/components/Button"
import Dropdown from "~/components/Dropdown"
import { AddIcon } from "~/components/Icon"
import ImageUploader from "~/components/ImageUploader"
import InputField, { useRichTextArea } from "~/components/InputField"

import Attachments from "../Attachments"
import Links from "../Links"
import { ButtonsGroup, ButtonsRow } from "../styles"
import QuizAnswer from "./QuizAnswer"

const defaultQuizAnswers = [{ text: "", isCorrect: true }]

type Props = {
	page: {
		id: number
		title: string
		text: string | null
		image: { filePath: string; contain: boolean } | null
		attachments: { id: number; fileType: string; label: string; url: string }[]
		links: { id: number; label: string; url: string }[]
		quiz: { answers: { text: string; isCorrect: boolean }[] | null } | null
	}
	isSaving: boolean
	isDeleting: boolean
	onClose(): void
	onSavePage: (data: {
		title: string
		text: string | null
		imageDataUrl: string | null | undefined
		containImage: boolean
		answers: { text: string; isCorrect: boolean }[]
	}) => void
	onDeletePage(): void
}
const QuizContent: FC<Props> = ({ page, isSaving, isDeleting, onClose, onSavePage, onDeletePage }) => {
	const [title, setTitle] = useState(page.title)
	const text = useRichTextArea({ label: "Text", initialValue: page.text, extendedFormatting: true })
	const image = useFile(page.id, page.image?.filePath ?? null)
	const [containImage, setContainImage] = useState(page.image?.contain ?? false)
	const [answers, setAnswers] = useState<{ text: string; isCorrect: boolean }[]>(
		page.quiz?.answers?.map(x => ({ text: x.text, isCorrect: x.isCorrect })) ?? defaultQuizAnswers,
	)

	useEffect(() => {
		setTitle(page.title)
		setContainImage(page.image?.contain ?? false)
		setAnswers(page.quiz?.answers?.map(x => ({ text: x.text, isCorrect: x.isCorrect })) ?? defaultQuizAnswers)
	}, [page])

	return (
		<>
			<InputField label="Title" value={title} onChange={setTitle} />
			{text.element}
			{answers.length > 0 && (
				<div className="border-component-border bg-component-background/40 flex flex-col gap-3 rounded-lg border p-3">
					<div className="flex items-center justify-between gap-3">
						<p className="text-sm font-semibold text-gray-900">Answer options</p>
						<p className="text-light shrink-0 text-xs font-medium">
							{answers.filter(answer => answer.isCorrect).length} correct
						</p>
					</div>
					{answers.map((answer, index) => (
						<QuizAnswer
							key={index}
							index={index}
							text={answer.text}
							isCorrect={answer.isCorrect}
							onTextChange={value =>
								setAnswers(current =>
									replaceArrayValue(current, index, { ...current[index], text: value }),
								)
							}
							onIsCorrectChange={value => {
								setAnswers(current =>
									replaceArrayValue(current, index, { ...current[index], isCorrect: value }),
								)
							}}
							onDelete={() =>
								setAnswers(current => [...current.slice(0, index), ...current.slice(index + 1)])
							}
						/>
					))}
				</div>
			)}
			<Button
				variant="secondary"
				className="w-full"
				onClick={() => setAnswers(current => [...current, { text: "", isCorrect: false }])}
			>
				<AddIcon className="size-5" /> Add answer option
			</Button>
			<ImageUploader label="Image" recommendedSize="thumbnail" {...image.imageUploader} />
			{image.filePath !== null && (
				<Dropdown
					items={[
						{ id: "cover", title: "Show as banner" },
						{ id: "contain", title: "Show full image" },
					]}
					selectedItemId={containImage ? "contain" : "cover"}
					onChange={id => setContainImage(id === "contain")}
				/>
			)}
			<Attachments pageId={page.id} attachments={page.attachments} />
			<Links pageId={page.id} links={page.links} />
			<ButtonsRow>
				<ButtonsGroup>
					<Button variant="secondary" onClick={onClose} isDisabled={isSaving || isDeleting}>
						Cancel
					</Button>
					<Button
						variant="primary"
						isDisabled={
							title.trim().length === 0 ||
							answers.length === 0 ||
							answers.every(x => !x.isCorrect) ||
							answers.some(x => x.text.trim().length === 0) ||
							isDeleting
						}
						isLoading={isSaving}
						onClick={async () =>
							onSavePage({
								title,
								text: await text.exportEditorState(),
								imageDataUrl: image.isModified ? await image.exportToDataUrl() : undefined,
								containImage,
								answers,
							})
						}
					>
						Save
					</Button>
				</ButtonsGroup>
				<Button variant="primary-danger" onClick={onDeletePage} isDisabled={isSaving} isLoading={isDeleting}>
					Delete
				</Button>
			</ButtonsRow>
		</>
	)
}

export default QuizContent
